import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useIsMobile } from '../../utils/useIsMobile';

const ROLES = {
  admin_general: 'Admin General',
  admin:         'Administrador',
  barbero:       'Barbero',
};

const COLOR_ROL = {
  admin_general: { fondo: 'rgba(124,58,237,0.10)', texto: '#6d28d9' },
  admin:         { fondo: 'rgba(37,99,235,0.10)',  texto: '#1d4ed8' },
  barbero:       { fondo: 'rgba(22,163,74,0.10)',  texto: '#15803d' },
};

const AVATARES = ['#6366f1', '#0ea5e9', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const colorAvatar = n => AVATARES[String(n || '').length % AVATARES.length];
const iniciales = n => String(n || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

// @duvanlopez a partir del nombre, solo como identificador visual.
const alias = n => '@' + String(n || '')
  .toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]/g, '');

const fmtFecha = (f) => {
  if (!f) return '—';
  const d = new Date(String(f).replace(' ', 'T'));
  return isNaN(d) ? '—' : d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const fmtHora = (f) => {
  if (!f) return '';
  const d = new Date(String(f).replace(' ', 'T'));
  return isNaN(d) ? '' : d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
};

export default function UsersTree({ negocios, onEditar, onAlternarEstado, onReiniciarClave, recarga }) {
  const esMovil = useIsMobile(900);

  const [negocioId, setNegocioId] = useState('');
  const [arbol, setArbol]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [cargando, setCargando] = useState(false);
  const [error, setError]     = useState('');
  const [buscar, setBuscar]   = useState('');
  const [rol, setRol]         = useState('');
  const [plegados, setPlegados] = useState({});   // { [idAdmin]: true }

  // Al entrar se selecciona el primer negocio: una pantalla vacia con un
  // desplegable sin elegir no dice nada.
  useEffect(() => {
    if (!negocioId && negocios.length) setNegocioId(String(negocios[0].id));
  }, [negocios, negocioId]);

  const cargar = useCallback(async () => {
    if (!negocioId) return;
    setCargando(true); setError('');
    try {
      const params = { negocio_id: negocioId };
      if (buscar) params.search = buscar;
      if (rol)    params.rol = rol;
      const r = await api.get('/users/admin/tree', { params });
      setArbol(r.data.administradores || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar la estructura de usuarios');
      setArbol([]); setTotal(0);
    } finally { setCargando(false); }
  }, [negocioId, buscar, rol]);

  useEffect(() => { cargar(); }, [cargar, recarga]);

  const alternar = (id) => setPlegados(p => ({ ...p, [id]: !p[id] }));

  const exportar = () => {
    const cab = ['Usuario', 'Rol', 'Estado', 'Correo', 'Registro', 'Creado por'];
    const filas = [];
    for (const admin of arbol) {
      filas.push([admin.nombre, ROLES[admin.rol] || admin.rol, admin.activo ? 'Activo' : 'Inactivo',
                  admin.email || '', fmtFecha(admin.creado_en), 'Dueño del negocio']);
      for (const h of admin.hijos) {
        filas.push([h.nombre, ROLES[h.rol] || h.rol, h.activo ? 'Activo' : 'Inactivo',
                    h.email || '', fmtFecha(h.creado_en), h.creado_por_nombre || '']);
      }
    }
    const csv = [cab, ...filas]
      .map(f => f.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = `estructura-usuarios-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const acciones = (u) => (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
      <button className="btn btn-secondary btn-icon" title="Editar" onClick={() => onEditar(u)}>✏️</button>
      <button className="btn btn-ghost btn-icon" title={u.activo ? 'Desactivar' : 'Activar'}
              onClick={() => onAlternarEstado(u)}>{u.activo ? '⛔' : '✅'}</button>
      <button className="btn btn-ghost btn-icon" title="Restablecer contraseña"
              onClick={() => onReiniciarClave(u)}>🔑</button>
    </div>
  );

  const estado = (u) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem',
                   color: u.activo ? '#16a34a' : '#ef4444', whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: u.activo ? '#16a34a' : '#ef4444' }} />
      {u.activo ? 'Activo' : 'Inactivo'}
    </span>
  );

  const etiquetaRol = (r) => {
    const c = COLOR_ROL[r] || { fondo: 'var(--color-surface-3)', texto: 'var(--color-text-muted)' };
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 999,
        background: c.fondo, color: c.texto,
        fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.04em',
        textTransform: 'uppercase', whiteSpace: 'nowrap',
      }}>{ROLES[r] || r}</span>
    );
  };

  const avatar = (u, tam) => (
    u.url_foto
      ? <img src={u.url_foto} alt="" style={{ width: tam, height: tam, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
      : <div style={{
          width: tam, height: tam, borderRadius: '50%', flexShrink: 0,
          background: colorAvatar(u.nombre), color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: tam <= 30 ? '0.68rem' : '0.78rem', fontWeight: 700,
        }}>{iniciales(u.nombre)}</div>
  );

  return (
    <>
      <div className="card" style={{ padding: '0.85rem 1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <strong style={{ fontSize: '0.9rem', marginRight: 'auto' }}>Estructura de usuarios por administrador</strong>

          <select className="form-select" style={{ margin: 0, flex: '0 1 210px' }}
                  value={negocioId} onChange={e => setNegocioId(e.target.value)}>
            {negocios.map(n => <option key={n.id} value={n.id}>{n.nombre || n.name}</option>)}
          </select>

          <input className="form-input" style={{ margin: 0, flex: '1 1 180px' }} placeholder="Buscar usuario..."
                 value={buscar} onChange={e => setBuscar(e.target.value)} />

          <select className="form-select" style={{ margin: 0, flex: '0 1 160px' }}
                  value={rol} onChange={e => setRol(e.target.value)}>
            <option value="">Todos los roles</option>
            {Object.entries(ROLES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>

          <button className="btn btn-secondary" onClick={exportar} disabled={!arbol.length}>⬇ Exportar</button>
        </div>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {cargando ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
        ) : arbol.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            Este negocio no tiene usuarios que coincidan
          </div>
        ) : (
          <div className="table-wrapper" style={{ border: 0, borderRadius: 0 }}>
            <table style={{ width: '100%', minWidth: esMovil ? 560 : 860 }}>
              <thead>
                <tr>
                  <th style={{ paddingLeft: 14 }}>Usuario</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  {!esMovil && <th>Correo</th>}
                  {!esMovil && <th>Registro</th>}
                  {!esMovil && <th>Creado por</th>}
                  <th className="col-acciones">Acciones</th>
                </tr>
              </thead>

              <tbody>
                {arbol.map(admin => {
                  const abierto = !plegados[admin.id];
                  return (
                    <React.Fragment key={admin.id}>
                      {/* Administrador */}
                      <tr>
                        <td style={{ paddingLeft: 14 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <button
                              onClick={() => alternar(admin.id)}
                              title={abierto ? 'Ocultar sus usuarios' : 'Ver sus usuarios'}
                              aria-expanded={abierto}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                width: 16, color: 'var(--color-text-muted)', fontSize: '0.7rem',
                                visibility: admin.hijos.length ? 'visible' : 'hidden',
                              }}
                            >{abierto ? '▼' : '▶'}</button>

                            {avatar(admin, 34)}

                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600 }}>{admin.nombre}</div>
                              <span style={{
                                display: 'inline-block', padding: '1px 7px', borderRadius: 999,
                                background: 'rgba(217,119,6,0.12)', color: '#b45309',
                                fontSize: '0.6rem', fontWeight: 700, letterSpacing: '.04em',
                                textTransform: 'uppercase', marginTop: 2,
                              }}>Administrador creador 👑</span>
                              <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{alias(admin.nombre)}</div>
                            </div>
                          </div>
                        </td>
                        <td>{etiquetaRol(admin.rol)}</td>
                        <td>{estado(admin)}</td>
                        {!esMovil && <td style={{ fontSize: '0.8rem', wordBreak: 'break-word' }}>{admin.email || '—'}</td>}
                        {!esMovil && (
                          <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {fmtFecha(admin.creado_en)}
                            <div style={{ color: 'var(--color-text-muted)', fontSize: '0.72rem' }}>{fmtHora(admin.creado_en)}</div>
                          </td>
                        )}
                        {!esMovil && (
                          <td style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                            —<div style={{ fontSize: '0.72rem' }}>Dueño del negocio</div>
                          </td>
                        )}
                        <td className="col-acciones">{acciones(admin)}</td>
                      </tr>

                      {/* Encabezado del grupo */}
                      {abierto && admin.hijos.length > 0 && (
                        <tr>
                          <td colSpan={esMovil ? 4 : 7} style={{
                            background: 'var(--color-surface-3)', paddingLeft: 52,
                            fontSize: '0.76rem', color: 'var(--color-text-muted)', fontWeight: 600,
                          }}>
                            👥 {admin.hijos.length} usuario{admin.hijos.length === 1 ? '' : 's'} creado{admin.hijos.length === 1 ? '' : 's'} por {admin.nombre}
                          </td>
                        </tr>
                      )}

                      {/* Usuarios creados */}
                      {abierto && admin.hijos.map(h => (
                        <tr key={h.id} style={{ background: 'var(--color-surface-2)' }}>
                          <td style={{ paddingLeft: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingLeft: 30 }}>
                              <span style={{ color: 'var(--color-border-light)', fontSize: '0.85rem' }}>↳</span>
                              {avatar(h, 28)}
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{h.nombre}</div>
                                <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{alias(h.nombre)}</div>
                              </div>
                            </div>
                          </td>
                          <td>{etiquetaRol(h.rol)}</td>
                          <td>{estado(h)}</td>
                          {!esMovil && <td style={{ fontSize: '0.8rem', wordBreak: 'break-word' }}>{h.email || '—'}</td>}
                          {!esMovil && (
                            <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                              {fmtFecha(h.creado_en)}
                              <div style={{ color: 'var(--color-text-muted)', fontSize: '0.72rem' }}>{fmtHora(h.creado_en)}</div>
                            </td>
                          )}
                          {!esMovil && (
                            <td style={{ fontSize: '0.8rem' }}>
                              {h.creado_por_nombre
                                ? <>↳ {h.creado_por_nombre}
                                    <div style={{ color: 'var(--color-text-muted)', fontSize: '0.72rem' }}>
                                      {fmtFecha(h.creado_en)}
                                    </div>
                                  </>
                                : <span style={{ color: 'var(--color-text-muted)' }}>
                                    —<div style={{ fontSize: '0.72rem' }}>Sin registro</div>
                                  </span>}
                            </td>
                          )}
                          <td className="col-acciones">{acciones(h)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{
          padding: '0.8rem 1rem', borderTop: '1px solid var(--color-border)',
          fontSize: '0.8rem', color: 'var(--color-text-muted)',
        }}>
          Mostrando {total} usuario{total === 1 ? '' : 's'} de este negocio
        </div>
      </div>

      <p style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: '1rem' }}>
        ⓘ Los usuarios creados antes de esta versión aparecen bajo el administrador del negocio,
        pero sin registro de quién los creó.
      </p>
    </>
  );
}
